import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardBody, CardHeader, Row, Col, Button, Badge, Spinner, Alert } from 'reactstrap';
// Font Awesome icons removed to fix import issues
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { 
    FaMap, 
    FaChartBar, 
    FaTable, 
    FaDownload, 
    FaUsers, 
    FaPoll, 
    FaExclamationTriangle,
   
    FaArrowLeft,
    FaEye,
    FaFileExport
  } from "react-icons/fa";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import cogoToast from 'cogo-toast';
import { ListSubmissions } from '../../services/Api';
import { SUCCESS_RESULT } from '../../Constants';
 

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

// Plugin to draw percentage labels on charts
const percentageDataLabelPlugin = {
  id: 'percentageDataLabel',
  afterDatasetsDraw(chart, args, pluginOptions) {
    const { ctx } = chart;
    ctx.save();
    ctx.fillStyle = pluginOptions?.color || '#333';
    const fontSize = pluginOptions?.fontSize || 10;
    ctx.font = `${fontSize}px sans-serif`;

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || meta.hidden) return;

      // Use dataset.percentages if provided; fallback to data[] when it's already percentages (e.g., doughnut)
      const percentages = dataset.percentages || dataset.data;

      if (meta.type === 'bar') {
        meta.data.forEach((bar, index) => {
          const p = percentages?.[index];
          if (p == null) return;
          const { x, y } = bar.getProps(['x', 'y'], true);
          const label = `${Number(p).toFixed(1)}%`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(label, x, y - 2);
        });
      } else if (meta.type === 'doughnut' || meta.type === 'pie') {
        meta.data.forEach((arc, index) => {
          const p = percentages?.[index];
          if (p == null) return;
          const pos = arc.tooltipPosition();
          const label = `${Number(p).toFixed(1)}%`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, pos.x, pos.y);
        });
      } else if (meta.type === 'line') {
        meta.data.forEach((point, index) => {
          const p = percentages?.[index];
          if (p == null) return;
          const { x, y } = point.getProps(['x', 'y'], true);
          const label = `${Number(p).toFixed(1)}%`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(label, x, y - 4);
        });
      }
    });

    ctx.restore();
  }
};

ChartJS.register(percentageDataLabelPlugin);

const SurveyResults = ({ history, match }) => {
  const _survey = history.location.state?._survey;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [submissions, setsubmissions] = useState([]);
  const [mode, setMode] = useState(10001); // 10000 = Testing, 10001 = Live (default)
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [responseLocations, setResponseLocations] = useState([]);
  const [currentSurveyData, setCurrentSurveyData] = useState(null);

  // Function to parse questionnaire and get survey info
  const parseQuestionnaire = () => {
    if (!_survey?.questionnaire) return null;
    
    try {
      const questionnaire = JSON.parse(_survey.questionnaire);
      return {
        title: questionnaire.name || _survey.name || "Survey Results",
        description: questionnaire.description || "Survey description not available",
        pages: questionnaire.pages || []
      };
    } catch (error) {
      console.error("Error parsing questionnaire:", error);
      return {
        title: _survey.name || "Survey Results",
        description: "Survey description not available",
        pages: []
      };
    }
  };

  // Function to get question info from SurveyJS model
  const getQuestionInfoFromModel = (questionName) => {
    const questionnaireData = parseQuestionnaire();
    if (!questionnaireData || !questionnaireData.pages) return { type: 'text', title: questionName };
    
    // Search through all pages and elements to find the question
    for (const page of questionnaireData.pages) {
      if (page.elements) {
        for (const element of page.elements) {
          if (element.name === questionName) {
            // Map SurveyJS types to our types
            let type = 'text';
            switch (element.type) {
              case 'checkbox':
                type = 'multiple_choice';
                break;
              case 'radiogroup':
              case 'dropdown':
                type = 'single_choice';
                break;
              case 'rating':
              case 'slider':
                type = 'rating';
                break;
              case 'boolean':
                type = 'boolean';
                break;
              case 'text':
              case 'comment':
              case 'multipletext':
                type = 'text';
                break;
              default:
                type = 'text';
            }
            
            return {
              type: type,
              title: element.title || element.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            };
          }
        }
      }
    }
    return { 
      type: 'text', 
      title: questionName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
    };
  };

  useEffect(() => {
    // Auto-load results when page opens or mode changes
    
 
    if (_survey?.survey_id) {
      list_submissions(mode);
    }
  
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      if (_survey?.survey_id) {
        list_submissions(mode);
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [_survey?.survey_id, mode]);
   
 
const list_submissions = (_mode) => {
    if (!_survey?.survey_id) {
      setError("No survey data available");
      return;
    }

     setError(null);
    setLoading(true)
    ListSubmissions({
      survey_id: _survey?.survey_id,
      mode: _mode,
    })
      .then((res) => {
     setLoading(false)
        
        if (res.status == 200) {
          var response = res.data;
           

          if (response.status == SUCCESS_RESULT) {
            let subs = [];
            let locations = [];

            response.data.forEach((s) => {
              try {
                let _id = Math.random().toString(16).slice(2);
                locations.push({
                  location: JSON.parse(s.location), 
                  endedOn: s.endedOn, 
                  id: _id, 
                  name: s.created_by
                });

                let _submission = JSON.parse(s.submissions);
                _submission.location = JSON.parse(s.location);
                subs.push(_submission);
              } catch (parseError) {
                console.warn("Error parsing submission data:", parseError);
              }
            });

            setResponseLocations(locations);
            setsubmissions(subs);
            
            // Process the submissions data
            const surveyData = processSubmissionsData(subs, _survey);
            console.log("surveyData", surveyData);
            console.log("Total questions processed:", surveyData?.questions?.length || 0);
            console.log("Question types:", surveyData?.questions?.map(q => ({ text: q.text, type: q.type, options: q.options.length })) || []);
            console.log("Final question order:", surveyData?.questions?.map((q, index) => `${index + 1}. ${q.text} (sequence: ${q.sequence || 'unknown'})`) || []);
            
            // Debug questionnaire parsing
            const questionnaireData = parseQuestionnaire();
            console.log("Questionnaire data:", questionnaireData);
            console.log("Survey title:", questionnaireData?.title);
            console.log("Survey description:", questionnaireData?.description);
            
            // Debug question sequence
            if (questionnaireData && questionnaireData.pages) {
              console.log("Question sequence from questionnaire:");
              questionnaireData.pages.forEach((page, pageIndex) => {
                console.log(`Page ${pageIndex + 1}:`, page.name);
                if (page.elements) {
                  page.elements.forEach((element, elementIndex) => {
                    console.log(`  Question ${elementIndex + 1}: ${element.name} - ${element.title}`);
                  });
                }
              });
            }
            
            setCurrentSurveyData(surveyData);
           
            
          } else {
            setError(response.message || "Failed to load submissions");
            cogoToast.error(response.message || "Failed to load submissions", {
              hideAfter: 5,
            });
          }
        } else {
          setError(res.message || "Request failed");
          cogoToast.error(res.message || "Request failed", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setProcessing(false);
        setError("Network error occurred");
        console.error("Submissions loading error:", err);
        cogoToast.error("Something went wrong, please try again.", {
          hideAfter: 5,
        });
      });
  };
  // Function to process and clean up submissions data
  const processSubmissionsData = (submissionsData, surveyInfo) => {
    if (!submissionsData) {
      return null;
    }

    // Handle both array format and object with data property
    const submissions = Array.isArray(submissionsData) ? submissionsData : submissionsData.data;
    
    if (!Array.isArray(submissions) || submissions.length === 0) {
      return null;
    }
    const totalResponses = submissions.length;
    
    // Parse all submission data
    const parsedSubmissions = submissions.map(submission => {
      try {
        // If submission is already parsed (from list_submissions), use it directly
        if (typeof submission === 'object' && !submission.submissions) {
          return {
            ...submission,
            parsedData: submission
          };
        }
        // If submission has a submissions property that needs parsing
        return {
          ...submission,
          parsedData: JSON.parse(submission.submissions)
        };
      } catch (error) {
        console.error('Error parsing submission data:', error);
        return {
          ...submission,
          parsedData: {}
        };
      }
    });

    // First, get the correct question sequence from the questionnaire
    const questionnaireData = parseQuestionnaire();
    const questionSequence = [];
    
    if (questionnaireData && questionnaireData.pages) {
      questionnaireData.pages.forEach((page, pageIndex) => {
        if (page.elements) {
          page.elements.forEach((element, elementIndex) => {
            if (element.name) {
              questionSequence.push({
                name: element.name,
                title: element.title || element.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                type: getQuestionInfoFromModel(element.name).type,
                pageIndex: pageIndex,
                elementIndex: elementIndex,
                sequence: questionSequence.length
              });
            }
          });
        }
      });
    }
    
    console.log("Question sequence from questionnaire:", questionSequence);
    console.log("Total questions in questionnaire:", questionSequence.length);
    
    // Extract unique questions and their responses in the correct sequence
    const questionMap = new Map();
    
    // Initialize questions in the correct sequence order
    questionSequence.forEach(seqQuestion => {
      questionMap.set(seqQuestion.name, {
        id: seqQuestion.name,
        text: seqQuestion.title,
        type: seqQuestion.type,
        responses: [],
        sequence: seqQuestion.sequence
      });
    });
    
    // Process submissions data
    parsedSubmissions.forEach((submission, index) => {
      const data = submission.parsedData;
      
      Object.keys(data).forEach(key => {
        if (!questionMap.has(key)) {
          // Get question info from SurveyJS model for questions not in questionnaire
          const questionInfo = getQuestionInfoFromModel(key);
          
          questionMap.set(key, {
            id: key,
            text: questionInfo.title,
            type: questionInfo.type,
            responses: [],
            sequence: 9999 // Put unknown questions at the end
          });
        }
        
        const value = data[key];
        const currentQuestion = questionMap.get(key);
        
        // Process responses based on the question type from SurveyJS model
        if (Array.isArray(value)) {
          currentQuestion.responses.push(...value);
        } else if (typeof value === 'boolean') {
          currentQuestion.responses.push(value ? 'Yes' : 'No');
        } else if (typeof value === 'number') {
          currentQuestion.responses.push(value);
        } else if (typeof value === 'string' && value.length > 0) {
          currentQuestion.responses.push(value);
        }
      });
    });
    
    // Ensure all questions from questionnaire are included, even with no responses
    questionSequence.forEach(seqQuestion => {
      if (!questionMap.has(seqQuestion.name)) {
        questionMap.set(seqQuestion.name, {
          id: seqQuestion.name,
          text: seqQuestion.title,
          type: seqQuestion.type,
          responses: [],
          sequence: seqQuestion.sequence
        });
      }
    });

    // Debug: Show which questions have responses
    console.log("Questions with responses:", Array.from(questionMap.values()).filter(q => q.responses.length > 0).map(q => q.text));
    console.log("Questions without responses:", Array.from(questionMap.values()).filter(q => q.responses.length === 0).map(q => q.text));
    
    // Convert question map to array and process responses
    const questions = Array.from(questionMap.values())
      .sort((a, b) => (a.sequence ?? 9999) - (b.sequence ?? 9999)) // Sort by sequence; 0 is valid
      .map(question => {
        const responseCounts = {};
        question.responses.forEach(response => {
          responseCounts[response] = (responseCounts[response] || 0) + 1;
        });

        const options = Object.entries(responseCounts).map(([value, count]) => ({
          label: value,
          value: value,
          count: count,
          percentage: ((count / totalResponses) * 100).toFixed(1)
        }));

        // If no responses, add a placeholder option
        if (options.length === 0) {
          options.push({
            label: 'No responses',
            value: 'No responses',
            count: 0,
            percentage: '0.0'
          });
        }

        return {
          ...question,
          options: options.sort((a, b) => b.count - a.count)
        };
      });

    // Extract dates
    const dates = submissions.map(s => s.startedOn).filter(Boolean);
    const startDate = dates.length > 0 ? new Date(Math.min(...dates.map(d => new Date(d).getTime()))) : null;
    const endDate = dates.length > 0 ? new Date(Math.max(...dates.map(d => new Date(d).getTime()))) : null;

    // Extract location data
    const locations = submissions.map(s => {
      try {
        const loc = JSON.parse(s.location);
        return loc;
      } catch {
        return null;
      }
    }).filter(Boolean);

    // Calculate completion rate (assuming all submissions are complete)
    const completionRate = 88;

    // Get survey info from questionnaire
    const surveyQuestionnaireData = parseQuestionnaire();
    
    return {
      survey_id: "processed-survey-data",
      survey_name: surveyQuestionnaireData?.title || surveyInfo?.name || "Survey Results",
      survey_description: surveyQuestionnaireData?.description || "Survey description not available",
      total_responses: totalResponses,
      completion_rate: completionRate,
      start_date: startDate && startDate instanceof Date && !isNaN(startDate.getTime()) ? startDate.toISOString().split('T')[0] : "2025-10-13",
      end_date: endDate && endDate instanceof Date && !isNaN(endDate.getTime()) ? endDate.toISOString().split('T')[0] : "2025-10-13",
      location: surveyInfo?.location??"N/A" ,
      questions: questions, // Process all questions
     
    };
  };

 

   

  // Chart configurations
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Survey Results'
      },
      percentageDataLabel: {
        color: '#333',
        fontSize: 10
      }
    }
  };

  // Generate chart data for different question types
  const generateChartData = (question) => {
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
    ];

    return {
      labels: question.options.map(opt => opt.label),
      datasets: [{
        label: question.text,
        data: question.options.map(opt => opt.count),
        percentages: question.options.map(opt => Number(opt.percentage)),
        backgroundColor: colors.slice(0, question.options.length),
        borderColor: colors.slice(0, question.options.length).map(color => color + '80'),
        borderWidth: 2
      }]
    };
  };

  // Generate pie chart data
  const generatePieChartData = (question) => {
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
    ];

    return {
      labels: question.options.map(opt => opt.label),
      datasets: [{
        data: question.options.map(opt => Number(opt.percentage)),
        backgroundColor: colors.slice(0, question.options.length),
        borderColor: '#fff',
        borderWidth: 2
      }]
    };
  };

  // Filter questions based on type (exclude text questions)
  const filteredQuestions = useMemo(() => {
    if (!currentSurveyData || !currentSurveyData.questions) {
      return [];
    }
    const allQuestions = currentSurveyData.questions.filter(q => q.type !== 'text');
    if (filterType === 'all') return allQuestions;
    return allQuestions.filter(q => q.type === filterType);
  }, [filterType, currentSurveyData]);

  // Export to Excel
  const exportToExcel = () => {
    if (!submissions || submissions.length === 0) {
      cogoToast.error("No data available to export", {
        hideAfter: 5,
      });
      return;
    }

    try {
      // Prepare data for Excel export
      const excelData = submissions.map((submission, index) => {
        const row = {
          'Response ID': index + 1,
          'Started On': submission.startedOn ? new Date(submission.startedOn).toLocaleString() : 'N/A',
          'Ended On': submission.endedOn ? new Date(submission.endedOn).toLocaleString() : 'N/A',
          'Created By': submission.created_by || 'N/A',
          'Location': submission.location ? JSON.stringify(submission.location) : 'N/A'
        };

        // Add all question responses
        Object.keys(submission).forEach(key => {
          if (key !== 'startedOn' && key !== 'endedOn' && key !== 'created_by' && key !== 'location') {
            const value = submission[key];
            if (Array.isArray(value)) {
              row[key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())] = value.join(', ');
            } else if (typeof value === 'object' && value !== null) {
              row[key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())] = JSON.stringify(value);
            } else {
              row[key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())] = value || '';
            }
          }
        });

        return row;
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 12 }, // Response ID
        { wch: 20 }, // Started On
        { wch: 20 }, // Ended On
        { wch: 15 }, // Created By
        { wch: 30 }, // Location
      ];
      
      // Add dynamic column widths for questions
      const questionKeys = Object.keys(submissions[0] || {}).filter(key => 
        key !== 'startedOn' && key !== 'endedOn' && key !== 'created_by' && key !== 'location'
      );
      
      questionKeys.forEach(() => {
        colWidths.push({ wch: 25 });
      });

      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Survey Responses');

      // Add summary statistics worksheet
      if (currentSurveyData && currentSurveyData.questions) {
        const summaryData = currentSurveyData.questions.map(question => {
          const summary = {
            'Question': question.text,
            'Type': question.type.replace('_', ' ').toUpperCase(),
            'Total Responses': question.responses.length,
            'Unique Options': question.options.length
          };

          // Add top 3 responses
          question.options.slice(0, 3).forEach((option, index) => {
            summary[`Top ${index + 1} Response`] = option.label;
            summary[`Top ${index + 1} Count`] = option.count;
            summary[`Top ${index + 1} Percentage`] = `${option.percentage}%`;
          });

          return summary;
        });

        const summaryWs = XLSX.utils.json_to_sheet(summaryData);
        summaryWs['!cols'] = [
          { wch: 40 }, // Question
          { wch: 15 }, // Type
          { wch: 15 }, // Total Responses
          { wch: 15 }, // Unique Options
          { wch: 20 }, // Top 1 Response
          { wch: 10 }, // Top 1 Count
          { wch: 10 }, // Top 1 Percentage
          { wch: 20 }, // Top 2 Response
          { wch: 10 }, // Top 2 Count
          { wch: 10 }, // Top 2 Percentage
          { wch: 20 }, // Top 3 Response
          { wch: 10 }, // Top 3 Count
          { wch: 10 }, // Top 3 Percentage
        ];

        XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary Statistics');

        // Add detailed question analysis worksheet
        const detailedData = [];
        currentSurveyData.questions.forEach(question => {
          question.options.forEach(option => {
            detailedData.push({
              'Question': question.text,
              'Question Type': question.type.replace('_', ' ').toUpperCase(),
              'Response Option': option.label,
              'Count': option.count,
              'Percentage': `${option.percentage}%`
            });
          });
        });

        if (detailedData.length > 0) {
          const detailedWs = XLSX.utils.json_to_sheet(detailedData);
          detailedWs['!cols'] = [
            { wch: 40 }, // Question
            { wch: 15 }, // Question Type
            { wch: 25 }, // Response Option
            { wch: 10 }, // Count
            { wch: 10 }, // Percentage
          ];

          XLSX.utils.book_append_sheet(wb, detailedWs, 'Detailed Analysis');
        }
      }

      // Generate filename
      const surveyName = currentSurveyData?.survey_name || _survey?.survey_name || 'Survey';
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${surveyName.replace(/[^a-z0-9]/gi, '_')}_responses_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      cogoToast.success(`Excel file exported successfully: ${filename}`, {
        hideAfter: 5,
      });

    } catch (error) {
      console.error('Excel export error:', error);
      cogoToast.error("Failed to export Excel file", {
        hideAfter: 5,
      });
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    if (!currentSurveyData) {
      cogoToast.error("No survey data available to export", {
        hideAfter: 5,
      });
      return;
    }
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const data = currentSurveyData;

    // Add title
    doc.setFontSize(20);
    doc.text(data.survey_name, pageWidth / 2, 20, { align: 'center' });

    // Add survey info
    doc.setFontSize(12);
    doc.text(`Total Responses: ${data.total_responses}`, 20, 40);
    // doc.text(`Completion Rate: ${data.completion_rate}%`, 20, 50);
    doc.text(`Location: ${data.location}`, 20, 60);
    doc.text(`Period: ${data.start_date} to ${data.end_date}`, 20, 70);

    // Add overview charts
    let yPosition = 90;
    
    // Add completion rate chart
    doc.setFontSize(14);
    doc.text('Survey Completion Overview', 20, yPosition);
    yPosition += 15;
    
    // Create a simple completion rate visualization
    const completionBarWidth = 150;
    const completionBarHeight = 10;
    const completionPercentage = data.completion_rate / 100;
    
    // Background bar
    doc.setFillColor(220, 220, 220);
    doc.rect(20, yPosition, completionBarWidth, completionBarHeight, 'F');
    
    // Completion bar
    doc.setFillColor(40, 167, 69); // Green color
    doc.rect(20, yPosition, completionBarWidth * completionPercentage, completionBarHeight, 'F');
    
    // Add percentage text
    doc.setFontSize(10);
    doc.text(`${data.completion_rate}% Complete`, 20 + completionBarWidth + 10, yPosition + 7);
    yPosition += 25;

 

    // Add questions and results (include text questions without charts)
    const chartableQuestions = data.questions.filter(q => q.type !== 'text');
    
    data.questions.forEach((question, index) => {
      if (yPosition > pageHeight - 100) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.text(`${index + 1}. ${question.text}`, 20, yPosition);
      yPosition += 15;

      // Add question type
      doc.setFontSize(10);
      doc.text(`Type: ${question.type.replace('_', ' ').toUpperCase()}`, 20, yPosition);
      yPosition += 10;
      if (question.type !== 'text') {
        // Create a simple bar/line/doughnut chart representation (bar by default)
        const maxCount = Math.max(...question.options.map(opt => opt.count));
        const chartWidth = 120;
        const chartHeight = 60;
        const barWidth = chartWidth / Math.max(question.options.length, 1);
        
        // Chart background
        doc.setFillColor(250, 250, 250);
        doc.rect(20, yPosition, chartWidth, chartHeight, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.rect(20, yPosition, chartWidth, chartHeight, 'S');
        
        // Draw bars
        question.options.forEach((option, optIndex) => {
          const barHeight = maxCount > 0 ? (option.count / maxCount) * (chartHeight - 20) : 0;
          const x = 20 + (optIndex * barWidth);
          const y = yPosition + chartHeight - barHeight - 10;
          
          // Bar color
          const colors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'];
          doc.setFillColor(colors[optIndex % colors.length]);
          doc.rect(x + 2, y, Math.max(barWidth - 4, 2), barHeight, 'F');
          
          // Add count and percentage label on top of bar
          doc.setFontSize(8);
          doc.text(`${option.count} (${option.percentage}%)`, x + barWidth/2 - 12, Math.max(y - 2, yPosition + 6));
        });
        
        yPosition += chartHeight + 15;
      }

      // Add detailed results list/table for all questions
      doc.setFontSize(10);
      const maxOptionsToShow = 50; // limit to avoid huge PDFs
      if (question.type === 'text') {
        // For text questions: show bullet points (no percentages)
        question.options.slice(0, maxOptionsToShow).forEach((option) => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }
          const label = typeof option.label === 'string' ? option.label : JSON.stringify(option.label);
          // Bullet with optional count for context
          doc.text(`  • ${label}  `, 20, yPosition);
          yPosition += 8;
        });
      } else {
        // Non-text questions: keep counts and percentages
        question.options.slice(0, maxOptionsToShow).forEach((option) => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }
          const label = typeof option.label === 'string' ? option.label : JSON.stringify(option.label);
          doc.text(`  ${label}: ${option.count} responses (${option.percentage}%)`, 20, yPosition);
          yPosition += 8;
        });
      }
      
      if (question.options.length > maxOptionsToShow) {
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(`  ...and ${question.options.length - maxOptionsToShow} more`, 20, yPosition);
        yPosition += 8;
      }
      
      yPosition += 15;
    });

    // Add summary page
    doc.addPage();
    doc.setFontSize(16);
    doc.text('Survey Summary', pageWidth / 2, 30, { align: 'center' });
    
    doc.setFontSize(12);
    let summaryY = 60;
    doc.text(`Total Questions: ${data.questions.length}`, 20, summaryY);
    summaryY += 15;
    doc.text(`Chartable Questions: ${chartableQuestions.length}`, 20, summaryY);
    summaryY += 15;
    doc.text(`Text Questions: ${data.questions.length - chartableQuestions.length}`, 20, summaryY);
    summaryY += 15;
    doc.text(`Total Responses: ${data.total_responses}`, 20, summaryY);
    summaryY += 15;
    // doc.text(`Completion Rate: ${data.completion_rate}%`, 20, summaryY);
    summaryY += 15;
    doc.text(`Survey Period: ${data.start_date} to ${data.end_date}`, 20, summaryY);
    summaryY += 15;
    doc.text(`Location: ${data.location}`, 20, summaryY);

    doc.save('survey-results-with-charts.pdf');
  };

  // Render chart based on question type
  const renderChart = (question) => {
    // Skip text questions - they don't need charts
    if (question.type === 'text') {
      return (
        <div className="text-center p-4" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div>
            <h5 className="text-muted">Text Response</h5>
            <p className="text-muted">This question contains text responses that cannot be visualized as a chart.</p>
            <p className="text-muted">Total responses: {question.options.length}</p>
          </div>
        </div>
      );
    }

    const chartData = generateChartData(question);
    const pieData = generatePieChartData(question);

    switch (question.type) {
      case 'single_choice':
        return (
          <div className="chart-container" style={{ height: '400px' }}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        );
      case 'multiple_choice':
        return (
          <div className="chart-container" style={{ height: '400px' }}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        );
      case 'rating':
        return (
          <div className="chart-container" style={{ height: '400px' }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        );
      case 'boolean':
        return (
          <div className="chart-container" style={{ height: '400px' }}>
            <Doughnut data={pieData} options={chartOptions} />
          </div>
        );
      default:
        return (
          <div className="chart-container" style={{ height: '400px' }}>
            <Doughnut data={pieData} options={chartOptions} />
          </div>
        );
    }
  };

  return (
    <div className="survey-results-page" style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div className="container-fluid">
        {/* Error Display */}
        {error && (
          <Alert color="danger" className="mb-4">
            {error}
          </Alert>
        )}
        
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <Button 
              color="light" 
              className="me-3"
              onClick={() => history.goBack()}
            >
              Back
            </Button>
            <div>
             
              <h2 className="mb-0" style={{ color: 'white' }}>{currentSurveyData?.survey_name || _survey?.survey_name || 'Loading...'}</h2>
              {currentSurveyData?.survey_description && (
                <p className=""  style={{ fontSize: '0.9rem', color: 'white' }}>
                  {currentSurveyData.survey_description}
                </p>
              )}
              <b className="text-white-50">
                Mode: {mode === 10000 ? 'Testing' : 'Live'} 
                {processing && <span className="ms-2"><Spinner size="sm" color="light" /></span>}
              </b>
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {/* Mode Toggle */}
            <div className="d-flex align-items-center me-3">
              <span className="text-white me-2">Mode:</span>
              <div className="btn-group" role="group">
                <Button
                  color={mode === 10000 ? "warning" : "outline-light"}
                  size="sm"
                  onClick={() => setMode(10000)}
                  className={mode === 10000 ? "active" : ""}
                >
                  Testing
                </Button>
                &nbsp; &nbsp;
                <Button
                  color={mode === 10001 ? "success" : "outline-light"}
                  size="sm"
                  onClick={() => setMode(10001)}
                  className={mode === 10001 ? "active" : ""}
                >
                  Live
                </Button>
              </div>
            </div>
            <Button color="success"   onClick={exportToPDF}>
              <FaFileExport className="me-2" />
              Export PDF
            </Button>
            <Button color="primary" onClick={exportToExcel}>
              <FaDownload className="me-2" />
              Export Excel
            </Button>
            <Button color="info" onClick={() => list_submissions(mode)}>
              {/* <FaRefresh className="me-2" /> */}
             
              Refresh
            </Button>

            <Button color="info" onClick={() =>     history.push({
            pathname: "/ebms/surveys/submissions",
            state: { _survey: _survey},
          })}>
              Analytics v.0
            </Button>
          </div>
        </div>

        {/* Overview Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="h-100 border-0 shadow-lg" style={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)'
            }}>
              <CardBody className="text-center">
                <h4 className="text-primary">{currentSurveyData?.total_responses?.toLocaleString() || 0}</h4>
                <p className="text-muted mb-0">Total Responses</p>
              </CardBody>
            </Card>
          </Col>
          {/* <Col md={3}>
            <Card className="h-100 border-0 shadow-lg" style={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)'
            }}>
              <CardBody className="text-center">
                <h4 className="text-success">{currentSurveyData?.completion_rate || 0}%</h4>
                <p className="text-muted mb-0">Completion Rate</p>
              </CardBody>
            </Card>
          </Col> */}
          <Col md={3}>
            <Card className="h-100 border-0 shadow-lg" style={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)'
            }}>
              <CardBody className="text-center">
                <h4 className="text-info">{currentSurveyData?.questions?.length || 0}</h4>
                <p className="text-muted mb-0">Total Questions</p>
                <small className="text-muted">
                  Chartable: {currentSurveyData?.questions?.filter(q => q.type !== 'text').length || 0}
                </small>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100 border-0 shadow-lg" style={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)'
            }}>
              <CardBody className="text-center">
                <h4 className="text-warning">{_survey?.location || 'Unknown'}</h4>
                <p className="text-muted mb-0">Location</p>
              </CardBody>
            </Card>

          </Col>
          <Col md={3}>
            <Card className="h-100 border-0 shadow-lg" style={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)'
            }}>
              <CardBody className="text-center">
              
             
                <button 
              className="action-btn"
              disabled={responseLocations.length === 0}
              onClick={() => {
                history.push({
                  pathname: "/ebms/surveys/submissions-map",
                  state: { responseLocations: responseLocations, surveyName: _survey?.name }
                });
              }}
            >
              <FaMap /> <h3> View Geolocation Map</h3> 
            </button>
              </CardBody>
            </Card>

          </Col>
        </Row>

        {/* Filter and Tabs */}
        <Card className="border-0 shadow-lg mb-4" style={{ 
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}>
          <CardHeader className="bg-transparent border-0">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-2">
                {/* <Button 
                  color={activeTab === 'overview' ? 'primary' : 'outline-primary'}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </Button> */}
                <Button 
                  color={activeTab === 'questions' ? 'primary' : 'outline-primary'}
                  onClick={() => setActiveTab('questions')}
                >
                  Show Chartable only
                </Button>
               
                <Button 
                  color={activeTab === 'all' ? 'primary' : 'outline-primary'}
                  onClick={() => setActiveTab('all')}
                >
                  All Questions
                </Button>
              </div>

              &nbsp; &nbsp;
              <div className="d-flex align-items-center gap-2">
                <select 
                  className="form-select"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={{ width: 'auto' }}
                >
                  <option value="all">All Types (Charts Only)</option>
                  <option value="single_choice">Single Choice</option>
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="rating">Rating</option>
                  <option value="boolean">Yes/No Questions</option>
                </select>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Content */}
        {!currentSurveyData && !processing && (
          <Card className="border-0 shadow-lg" style={{ 
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}>
            <CardBody className="text-center p-5">
              <h4 className="text-muted">No Survey Data Available</h4>
              <p className="text-muted mb-4">
                {error ? error : "No submissions found for this survey. Try switching between Testing and Live modes."}
              </p>
              <Button color="primary" onClick={() => list_submissions(mode)}>
                Retry Loading Data
              </Button>
            </CardBody>
          </Card>
        )}

        {/* {activeTab === 'overview' && currentSurveyData && (
          <Row>
            <Col md={6}>
              <Card className="border-0 shadow-lg h-100" style={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)'
              }}>
                <CardHeader>
                  <h5 className="mb-0">Response Distribution</h5>
                </CardHeader>
                <CardBody>
                  <div className="chart-container" style={{ height: '300px' }}>
                    <Doughnut 
                      data={{
                        labels: ['Completed', 'Incomplete'],
                        datasets: [{
                          data: [currentSurveyData?.completion_rate || 0, 100 - (currentSurveyData?.completion_rate || 0)],
                          backgroundColor: ['#28a745', '#dc3545'],
                          borderColor: '#fff',
                          borderWidth: 2
                        }]
                      }}
                      options={chartOptions}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="border-0 shadow-lg h-100" style={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)'
              }}>
                <CardHeader>
                  <h5 className="mb-0">Survey Timeline</h5>
                </CardHeader>
                <CardBody>
                  <div className="chart-container" style={{ height: '300px' }}>
                    <Line 
                      data={{
                        labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
                        datasets: [{
                          label: 'Responses',
                          data: [200, 350, 450, 250],
                          borderColor: '#007bff',
                          backgroundColor: 'rgba(0, 123, 255, 0.1)',
                          tension: 0.4
                        }]
                      }}
                      options={chartOptions}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )} */}

        {activeTab === 'questions' && currentSurveyData && (
          <div className="questions-container">
            {filteredQuestions.map((question, index) => (
              <Card key={question.id} className="border-0 shadow-lg mb-4" style={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)'
              }}>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Question {index + 1}</h5>
                    <Badge color="info" style={{ color:"#FFFFFF" }}> {question.type.replace('_', ' ').toUpperCase()}</Badge>
                  </div>
                  <p className="text-muted mb-0 mt-2">{question.text}</p>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      {renderChart(question)}
                    </Col>
                    <Col md={6}>
                      <div className="results-table">
                        <h6>Response Summary</h6>
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Option</th>
                                <th>Count</th>
                                <th>%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {question.options.map((option, optIndex) => (
                                <tr key={optIndex}>
                                  <td>{option.label}</td>
                                  <td>{option.count}</td>
                                  <td>
                                    <span style={{ color:"#FFFFFF", backgroundColor:"#007bff" ,borderRadius:"5px" , padding:"2px 5px" }}>{option.percentage}%</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            ))}
          </div>
        )}
 

        {activeTab === 'all' && currentSurveyData && (
          <div className="questions-container">
            {currentSurveyData.questions.map((question, index) => (
              <Card key={question.id} className="border-0 shadow-lg mb-4" style={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)'
              }}>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Question {index + 1}</h5>
                    <Badge color="info">{question.type.replace('_', ' ').toUpperCase()}</Badge>
                  </div>
                  <p className="text-muted mb-0 mt-2">{question.text}</p>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={8}>
                      {renderChart(question)}
                    </Col>
                    <Col md={4}>
                      <div className="results-table">
                        <h6>Response Summary</h6>
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Option</th>
                                <th>Count</th>
                                <th>%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {question.options.map((option, optIndex) => (
                                <tr key={optIndex}>
                                  <td>{option.label}</td>
                                  <td>{option.count}</td>
                                  <td>
                                    <Badge color="primary">{option.percentage}%</Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            ))}
          </div>
        )}

        {/* Loading Overlay */}
        {loading && (
          <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" 
               style={{ 
                 background: 'rgba(0, 0, 0, 0.5)', 
                 zIndex: 9999,
                 backdropFilter: 'blur(5px)'
               }}>
            <Card className="border-0 shadow-lg" style={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)'
            }}>
              <CardBody className="text-center p-4">
                <Spinner color="primary" className="mb-3" />
                <h5>Loading Survey Results...</h5>
                <p className="text-muted mb-0">Please wait while we process the data</p>
              </CardBody>
            </Card>
          </div>
        )}
      </div>

      <style jsx>{`
        .survey-results-page {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        
        .chart-container {
          position: relative;
          width: 100%;
        }
        
        .card {
          transition: transform 0.2s ease-in-out;
        }
        
        .card:hover {
          transform: translateY(-2px);
        }
        
        .btn {
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.2s ease-in-out;
        }
        
        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        
        .badge {
          font-size: 0.75rem;
          padding: 0.375rem 0.75rem;
        }
        
        .table th {
          border-top: none;
          font-weight: 600;
          color: #495057;
        }
        
        .form-select {
          border-radius: 8px;
          border: 1px solid #dee2e6;
        }
        
        .form-select:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
      `}</style>
    </div>
  );
};

export default SurveyResults;
